import SimulationDetails from 'components/sales-details/proposition-detail-cards/SimulationDetails'
import ContractDetails from 'components/sales-details/proposition-detail-cards/ContractDetails'
import styles from './Details.module.scss'
import { PropositionDetailsProps } from './types'
import { useState } from 'react'
import EditSimulationDetails from 'components/sales-details/proposition-detail-cards/EditSimulationDetails'
import { Button, Heading, Icon, popErrorToast, popSuccessToast, Tooltip } from '@boltenergy-be/design-system'
import CollapsibleCard from 'components/collapsible-card/CollapsibleCard.tsx'
import DataBlock from 'features/contracts/add/components/data-block/DataBlock.tsx'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { useUpdateOpportunityMutation } from 'store/queries/bolt-api/contacts'
import EditingButton from 'components/editing-button/EditingButton'
import NewCaseDrawer from 'components/sales-details/new-case-drawer/NewCaseDrawer'
import ConfirmationTooltip from 'components/confirmation-tooltip/ConfirmationTooltip'
import classNames from 'classnames'
import { Opportunity, OpportunityStage } from 'types/contacts.ts'
import { removeNonAlphanumericals } from 'utils/format.ts'
import { SIMULATION_TYPES } from '@boltenergy-be/shared'
import { useResendMailMutation } from 'store/queries/bolt-backend/prospects'

const PropositionDetails = ({
  opportunity,
  contract,
  contactId,
  accountId,
  setActiveEditing,
  propositionNumber,
  identifier,
  activeEditing,
  customerName,
  salesParameters
}: PropositionDetailsProps) => {
  // i18n
  const { t } = useTranslation(['sales', 'common', 'errors'])

  // Local constant
  const isEditing = activeEditing === identifier

  // Local state
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Mutation
  const [updateOpportunity, { isLoading: updateOpportunityIsLoading }] = useUpdateOpportunityMutation()
  const [resendMail, { isLoading: resendMailIsLoading }] = useResendMailMutation()

  // React Hook Form
  const hookForm = useForm<Partial<Opportunity>>({
    mode: 'onBlur',
    defaultValues: {
      forcedAmount: opportunity.forcedAmount,
      ibanNumber: removeNonAlphanumericals(opportunity.ibanNumber ?? ''),
      paymentMode: opportunity.paymentMode,
      address: {
        postalCode: opportunity.address.postalCode,
        streetName: opportunity.address.streetName,
        streetNumber: opportunity.address.streetNumber,
        streetBox: opportunity.address.streetBox,
        townName: opportunity.address.townName
      }
    }
  })
  const { handleSubmit, reset } = hookForm

  /**
   * Updates the Opportunity object in the API
   *
   * @param {Partial<Opportunity>} data
   */
  const handleOpportunityUpdate = async (data: Partial<Opportunity>) => {
    try {
      // Update the opportunity
      const updatedOpportunity = await updateOpportunity({
        opportunity: { ...data, sfId: opportunity.sfId },
        accountId,
        contactId
      }).unwrap()

      // Check if the opportunity was updated
      if (updatedOpportunity) {
        // Remove the editing state
        setActiveEditing(undefined)

        // Show success toast
        popSuccessToast(t(($) => $.details.toast.success))
      } else {
        popErrorToast(
          t(($) => $.default, {
            ns: 'errors'
          })
        )
      }
    } catch (error) {
      popErrorToast(
        t(($) => $.default, {
          ns: 'errors'
        })
      )
    }
  }

  /**
   * Handles the resend opportunity email
   *
   * @returns {Promise<void>}
   */
  const handleResendOpportunityEmail = async () => {
    try {
      const result = await resendMail({ opportunityId: opportunity.sfId })
      if (result?.data) {
        popSuccessToast(t(($) => $.details.toast.resend))
      } else {
        popErrorToast(
          t(($) => $.default, {
            ns: 'errors'
          })
        )
      }
    } catch (e) {
      popErrorToast(
        t(($) => $.default, {
          ns: 'errors'
        })
      )
    }
  }

  return (
    <section className={styles.proposition}>
      <header className={styles.heading}>
        <Heading as="h3">
          {t(($) => $.details.simulation.simulation)} {propositionNumber}
        </Heading>

        <menu className={classNames(styles.actions)}>
          <EditingButton
            isDisabled={
              (opportunity.stage !== OpportunityStage.REGISTRATION_STARTED && opportunity.stage !== OpportunityStage.TO_BE_CONFIRMED) ||
              !!activeEditing
            }
            setIsEditing={setActiveEditing}
            isLoading={updateOpportunityIsLoading}
            onSubmit={handleSubmit(handleOpportunityUpdate)}
            onCancel={reset}
            {...{ identifier, isEditing }}
          />

          {!isEditing && (
            <>
              <ConfirmationTooltip
                label={t(($) => $.details.emailModal.optIn)}
                confirmation={t(($) => $.details.emailModal.confirm)}
                onConfirm={handleResendOpportunityEmail}
                disabled={
                  !!activeEditing ||
                  ![
                    OpportunityStage.REGISTRATION_STARTED,
                    OpportunityStage.TO_BE_CONFIRMED,
                    OpportunityStage.NO_EAN,
                    OpportunityStage.CLOSED_WON
                  ].includes(opportunity.stage) ||
                  (opportunity.stage === OpportunityStage.REGISTRATION_STARTED &&
                    opportunity.simulationType !== SIMULATION_TYPES.pricingSimulation)
                }
                isLoading={resendMailIsLoading}
              >
                <Tooltip tooltipContent={t(($) => $.details.tooltips.resend)} asChild>
                  <span>
                    <Icon name="envelope" />
                  </span>
                </Tooltip>
              </ConfirmationTooltip>

              <ConfirmationTooltip
                label={t(($) => $.details.closeOpportunityModal.optIn)}
                confirmation={t(($) => $.confirm, {
                  ns: 'common'
                })}
                onConfirm={() => handleOpportunityUpdate({ stage: OpportunityStage.CLOSED_LOST })}
                disabled={
                  ![OpportunityStage.TO_BE_CONFIRMED, OpportunityStage.NO_EAN, OpportunityStage.REGISTRATION_STARTED].includes(
                    opportunity.stage
                  ) || !!activeEditing
                }
                isLoading={updateOpportunityIsLoading}
              >
                <Tooltip tooltipContent={t(($) => $.details.tooltips.close)} asChild>
                  <span>
                    <Icon name="crossCircle" />
                  </span>
                </Tooltip>
              </ConfirmationTooltip>

              <Button variant="tertiary" disabled={!!activeEditing || !contract} onClick={() => setDrawerOpen(true)}>
                <Tooltip tooltipContent={t(($) => $.details.tooltips.new)} asChild>
                  <span>
                    <Icon name="chatExclamation" />
                  </span>
                </Tooltip>
              </Button>
            </>
          )}
        </menu>
      </header>

      <CollapsibleCard
        titleSection={
          <Heading as="h4" className={styles.title}>
            {t(($) => $.details.simulation.title)}
          </Heading>
        }
        contentClassName={styles['details-block']}
      >
        <DataBlock label={t(($) => $.details.simulation.stage)} withMargin={false}>
          {opportunity.stage}
        </DataBlock>
        <DataBlock label={t(($) => $.details.simulation.signingMethod)} withMargin={false}>
          {opportunity.signChannel}
        </DataBlock>
        <DataBlock label={t(($) => $.details.simulation.registrationOrigin)} withMargin={false}>
          {opportunity.eventName}
        </DataBlock>
        <DataBlock label={t(($) => $.details.simulation.secondAddress)} withMargin={false}>
          {opportunity.extraAddressFlow
            ? t(($) => $.yes, {
                ns: 'common'
              })
            : t(($) => $.no, {
                ns: 'common'
              })}
        </DataBlock>
        <DataBlock label={t(($) => $.details.simulation.simulationType)} withMargin={false}>
          {opportunity.simulationType}
        </DataBlock>

        {isEditing ? (
          <FormProvider {...hookForm}>
            <EditSimulationDetails {...{ opportunity }} />
          </FormProvider>
        ) : (
          <SimulationDetails {...{ opportunity }} />
        )}
      </CollapsibleCard>

      {contract && <ContractDetails contract={contract.serviceContracts.electricity} />}

      {contract?.serviceContracts?.gas && <ContractDetails contract={contract.serviceContracts.gas} />}

      <NewCaseDrawer
        opportunityId={opportunity.sfId}
        {...{ accountId, customerName, contactId, salesParameters, drawerOpen, setDrawerOpen }}
      />
    </section>
  )
}

export default PropositionDetails
